import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  MultiSelect,
  ScrollArea,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconEye,
  IconFileCheck,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useAddendumDraft,
  useAddendumOptions,
  useDeleteAddendumDraft,
  useSaveAddendumDraft,
  useSignOutAddendum,
} from "../../hooks/useAddendum";
import type {
  AddendumDraftAccess,
  AddendumDraft,
  AddendumDraftInput,
} from "../../services/addendum.service";
import type { ReportPreviewEvidence } from "../../services/signOff.service";
import {
  reflexPanelsService,
  type ReflexPanelProposal,
  type TissueCypherSignOutProposal,
} from "../../services/reflexPanels.service";
import { findUnresolvedAddendumVariables } from "../../utils/addendumVariables";
import { isEmptyHtml } from "../../utils/helpers";
import {
  scrollToFirstError,
  showValidationNotification,
} from "../../utils/formValidation";
import { SignOffAuthModal } from "../workflows/SignOffAuthModal";
import { AddendumReportPreviewModal } from "./AddendumReportPreviewModal";
import { ConfirmAddendumActionModal } from "./ConfirmAddendumActionModal";
import ReflexPanelDecisionModal from "./ReflexPanelDecisionModal";
import RichTextDiagnosis from "./RichTextDiagnosis";
import { AddendumCptQuantityRows } from "./AddendumCptQuantityRows";
import {
  type AdditionalCptCodeQuantities,
  isValidAdditionalCptQuantity,
  normalizeAdditionalCptQuantities,
  reconcileAdditionalCptQuantities,
} from "./addendumCptQuantities";

interface AddendumEditorModalProps {
  opened: boolean;
  caseId: string;
  caseNumber: string;
  sessionId?: string | null;
  onClose: () => void;
  onSigned?: () => void;
  onSessionUpdated?: (sessionId: string, expiresAt?: string) => void;
  reviewRequired?: boolean;
  draftOnly?: boolean;
  access?: AddendumDraftAccess;
}

interface AddendumFormValues {
  contentHtml: string;
  sourceMacroId: string | null;
  additionalCptCodeIds: string[];
  additionalCptCodeQuantities: AdditionalCptCodeQuantities;
  additionalIcdCodeIds: string[];
}

function draftInput(
  values: AddendumFormValues,
  revision?: number
): AddendumDraftInput {
  const additionalCptCodeQuantities = normalizeAdditionalCptQuantities(
    values.additionalCptCodeIds,
    values.additionalCptCodeQuantities
  );
  return {
    ...values,
    additionalCptCodeQuantities,
    revision,
  };
}

export function AddendumEditorModal({
  opened,
  caseId,
  caseNumber,
  sessionId,
  onClose,
  onSigned,
  onSessionUpdated,
  reviewRequired = true,
  draftOnly = false,
  access = "ordinary",
}: AddendumEditorModalProps) {
  const draftQuery = useAddendumDraft(caseId, opened, access);
  const optionsQuery = useAddendumOptions(caseId, opened, access);
  const saveDraft = useSaveAddendumDraft(caseId, access);
  const deleteDraft = useDeleteAddendumDraft(caseId);
  const signOut = useSignOutAddendum(caseId);
  const [authenticationOpened, setAuthenticationOpened] = useState(false);
  const [authenticationError, setAuthenticationError] = useState<string | null>(
    null
  );
  const [draftPendingSignature, setDraftPendingSignature] =
    useState<AddendumDraft | null>(null);
  const [evidencePendingSignature, setEvidencePendingSignature] = useState<
    ReportPreviewEvidence | undefined
  >();
  const [previewDraft, setPreviewDraft] = useState<AddendumDraft | null>(null);
  const [previewOpened, setPreviewOpened] = useState(false);
  const [panelDecisionOpened, setPanelDecisionOpened] = useState(false);
  const [panelDecisionSubmitting, setPanelDecisionSubmitting] = useState(false);
  const [panelDecisionDraft, setPanelDecisionDraft] =
    useState<AddendumDraft | null>(null);
  const [reflexProposals, setReflexProposals] = useState<ReflexPanelProposal[]>(
    []
  );
  const [tissueCypherProposals, setTissueCypherProposals] = useState<
    TissueCypherSignOutProposal[]
  >([]);
  const [reviewedPanelKeys, setReviewedPanelKeys] = useState<string[]>([]);
  const [deleteConfirmationOpened, setDeleteConfirmationOpened] =
    useState(false);
  const [discardConfirmationOpened, setDiscardConfirmationOpened] =
    useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [hydratedCaseId, setHydratedCaseId] = useState<string | null>(null);
  const addendumEditorRef = useRef<HTMLDivElement>(null);

  const form = useForm<AddendumFormValues>({
    initialValues: {
      contentHtml: "",
      sourceMacroId: null,
      additionalCptCodeIds: [],
      additionalCptCodeQuantities: {},
      additionalIcdCodeIds: [],
    },
    validateInputOnBlur: true,
    validate: {
      contentHtml: (value) =>
        isEmptyHtml(value) ? "Enter the addendum report text" : null,
      additionalIcdCodeIds: (value, values) =>
        values.additionalCptCodeIds.length > 0 && value.length > 4
          ? "Select at most four ICD-10 codes when adding CPT lines"
          : null,
      additionalCptCodeQuantities: (value, values) =>
        values.additionalCptCodeIds.every((id) =>
          isValidAdditionalCptQuantity(value[id] ?? 1)
        )
          ? null
          : "Enter a whole number from 1 to 99 for each CPT code",
    },
  });
  const { clearErrors, resetDirty, setValues } = form;

  useEffect(() => {
    if (!opened) {
      setHydratedCaseId(null);
    }
  }, [opened]);

  useEffect(() => {
    if (
      !opened ||
      hydratedCaseId === caseId ||
      !draftQuery.isSuccess ||
      draftQuery.isFetching ||
      draftQuery.data === undefined
    ) {
      return;
    }
    const draft = draftQuery.data;
    setValues({
      contentHtml: draft?.contentHtml || "",
      sourceMacroId: draft?.sourceMacroId || null,
      additionalCptCodeIds: draft?.additionalCptCodeIds || [],
      additionalCptCodeQuantities: Object.fromEntries(
        (draft?.additionalCptCodeIds || []).map((id) => [
          id,
          draft?.additionalCptCodeQuantities?.[id] || 1,
        ])
      ),
      additionalIcdCodeIds: draft?.additionalIcdCodeIds || [],
    });
    resetDirty();
    clearErrors();
    setFormError(null);
    setHydratedCaseId(caseId);
  }, [
    caseId,
    clearErrors,
    draftQuery.data,
    draftQuery.isFetching,
    draftQuery.isSuccess,
    hydratedCaseId,
    opened,
    resetDirty,
    setValues,
  ]);

  const macroOptions = useMemo(
    () =>
      (optionsQuery.data?.macros || []).map((macro) => ({
        value: macro.id,
        label: macro.shortcut
          ? `${macro.name} (${macro.shortcut})`
          : macro.name,
      })),
    [optionsQuery.data?.macros]
  );
  const cptOptions = useMemo(
    () =>
      (optionsQuery.data?.cptCodes || []).map((code) => ({
        value: code.id,
        label: `${code.code} — ${code.description}`,
      })),
    [optionsQuery.data?.cptCodes]
  );
  const icdOptions = useMemo(
    () =>
      (optionsQuery.data?.icdCodes || []).map((code) => ({
        value: code.id,
        label: `${code.code} — ${code.description}`,
      })),
    [optionsQuery.data?.icdCodes]
  );
  const isBusy =
    saveDraft.isPending || deleteDraft.isPending || signOut.isPending;
  const isDraftHydrated = opened && hydratedCaseId === caseId;
  const loadError = draftQuery.error || optionsQuery.error;
  const displayedError =
    formError ||
    (loadError instanceof Error
      ? loadError.message
      : loadError
      ? "The addendum data could not be loaded."
      : null);
  const editorGuidance = useMemo(
    () =>
      draftOnly
        ? "Save this addendum as a draft for the assigned pathologist to review."
        : "Save freely as a draft. The report changes only when you authenticate and select Sign Out Addendum.",
    [draftOnly]
  );

  const handleContentChange = useCallback(
    (contentHtml: string) => {
      form.setFieldValue("contentHtml", contentHtml);
      form.clearFieldError("contentHtml");
    },
    [form]
  );

  const handleCptCodesChange = useCallback(
    (selectedIds: string[]) => {
      form.setFieldValue("additionalCptCodeIds", selectedIds);
      form.setFieldValue(
        "additionalCptCodeQuantities",
        reconcileAdditionalCptQuantities(
          form.values.additionalCptCodeIds,
          selectedIds,
          form.values.additionalCptCodeQuantities
        )
      );
      Object.keys(form.values.additionalCptCodeQuantities)
        .filter((id) => !selectedIds.includes(id))
        .forEach((id) =>
          form.clearFieldError(`additionalCptCodeQuantities.${id}`)
        );
    },
    [form]
  );
  const handleCptQuantityChange = useCallback(
    (codeId: string, value: number | string) => {
      form.setFieldValue(`additionalCptCodeQuantities.${codeId}`, value);
    },
    [form]
  );
  const handleCptQuantityBlur = useCallback(() => {
    form.validateField("additionalCptCodeQuantities");
  }, [form]);

  const focusFirstAddendumVariable = useCallback(() => {
    setTimeout(() => {
      const editorRoot = addendumEditorRef.current;
      const firstVariable = editorRoot?.querySelector<HTMLElement>(
        "[data-variable-input]"
      );
      if (firstVariable) {
        firstVariable.focus();
        return;
      }
      editorRoot?.querySelector<HTMLElement>(".ProseMirror")?.focus();
    }, 150);
  }, []);

  const handleMacroChange = useCallback(
    (macroId: string | null) => {
      form.setFieldValue("sourceMacroId", macroId);
      const macro = optionsQuery.data?.macros.find(
        (candidate) => candidate.id === macroId
      );
      if (macro) {
        form.setFieldValue("contentHtml", macro.contentHtml);
        form.clearFieldError("contentHtml");
        focusFirstAddendumVariable();
      }
    },
    [focusFirstAddendumVariable, form, optionsQuery.data?.macros]
  );

  const persistDraft = useCallback(async (): Promise<AddendumDraft | null> => {
    const validation = form.validate();
    if (validation.hasErrors) {
      showValidationNotification("Complete the required addendum fields.");
      scrollToFirstError(form);
      return null;
    }
    const currentDraft = draftQuery.data;
    const saved = await saveDraft.mutateAsync({
      draftId: currentDraft?.id,
      input: draftInput(form.values, currentDraft?.revision),
    });
    form.resetDirty();
    return saved;
  }, [draftQuery.data, form, saveDraft]);

  const completeSignature = useCallback(
    async (
      draft: AddendumDraft,
      options: {
        password?: string;
        previewEvidence?: ReportPreviewEvidence;
        reviewedKeys?: string[];
      } = {}
    ) => {
      const {
        password,
        previewEvidence,
        reviewedKeys = reviewedPanelKeys,
      } = options;
      try {
        const result = await signOut.mutateAsync({
          draftId: draft.id,
          revision: draft.revision,
          sessionId,
          password,
          previewEvidence,
          reviewedPanelKeys: reviewedKeys,
        });
        setAuthenticationOpened(false);
        setAuthenticationError(null);
        const deliveryMessage = result.deliveryQueueFailed
          ? "Automatic delivery could not be queued. Retry it from report delivery."
          : result.deliveryQueue.length > 0
          ? `Delivery was queued to ${result.deliveryQueue.length} destination(s).`
          : "No automatic delivery destination was queued.";
        const message = result.pdfGenerationQueueFailed
          ? `${deliveryMessage} PDF generation also needs a retry.`
          : deliveryMessage;
        notifications.show({
          title: "Addendum signed out",
          message: `Report version ${result.reportVersion} was created. ${message}`,
          color:
            result.deliveryQueueFailed || result.pdfGenerationQueueFailed
              ? "yellow"
              : "green",
        });
        onSessionUpdated?.(result.sessionId, result.sessionExpiresAt);
        onSigned?.();
        onClose();
      } catch (error) {
        const response = (
          error as Error & {
            response?: { data?: { requiresPassword?: boolean } };
          }
        ).response;
        if (response?.data?.requiresPassword) {
          setDraftPendingSignature(draft);
          setEvidencePendingSignature(previewEvidence);
          setAuthenticationError(
            password ? "The password was not accepted. Try again." : null
          );
          setAuthenticationOpened(true);
          return;
        }
        const reviewErrorCode = (
          error as Error & {
            response?: {
              data?: {
                code?: string;
                reviewErrorCode?: string;
                sessionId?: string;
                sessionExpiresAt?: string;
              };
            };
          }
        ).response?.data;
        if (
          reviewErrorCode?.code === "REPORT_REVIEW_REQUIRED" ||
          reviewErrorCode?.code === "REPORT_REVIEW_STALE" ||
          reviewErrorCode?.reviewErrorCode === "REPORT_REVIEW_REQUIRED" ||
          reviewErrorCode?.reviewErrorCode === "REPORT_REVIEW_STALE"
        ) {
          setAuthenticationOpened(false);
          if (reviewErrorCode.sessionId) {
            onSessionUpdated?.(
              reviewErrorCode.sessionId,
              reviewErrorCode.sessionExpiresAt
            );
          }
          setPreviewDraft(draft);
          setPreviewOpened(true);
          setFormError(
            "The report changed or needs review. Review the current addendum preview before signing out."
          );
          return;
        }
        setFormError(
          error instanceof Error
            ? error.message
            : "The addendum could not be signed."
        );
      }
    },
    [onClose, onSessionUpdated, onSigned, reviewedPanelKeys, sessionId, signOut]
  );

  const handleSave = useCallback(async () => {
    setFormError(null);
    try {
      const saved = await persistDraft();
      if (!saved) return;
      notifications.show({
        title: "Draft saved",
        message: "No report version or delivery was created.",
        color: "green",
      });
      if (draftOnly) onClose();
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : "The draft could not be saved."
      );
    }
  }, [draftOnly, onClose, persistDraft]);

  const handlePreview = useCallback(async () => {
    setFormError(null);
    try {
      const saved = await persistDraft();
      if (!saved) return;
      setPreviewDraft(saved);
      setPreviewOpened(true);
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "The addendum preview could not be prepared."
      );
    }
  }, [persistDraft]);

  const handleSignOut = useCallback(async () => {
    setFormError(null);
    const unresolved = findUnresolvedAddendumVariables(form.values.contentHtml);
    if (unresolved.length > 0) {
      setFormError(
        `Complete these variables before signing: ${unresolved.join(", ")}`
      );
      return;
    }
    try {
      const saved = await persistDraft();
      if (!saved) return;
      const [reflex, tissueCypher] = await Promise.all([
        reflexPanelsService.getProposals(caseId),
        reflexPanelsService.getTissueCypherProposals(caseId, saved.id),
      ]);
      if (reflex.length > 0 || tissueCypher.length > 0) {
        setPanelDecisionDraft(saved);
        setReflexProposals(reflex);
        setTissueCypherProposals(tissueCypher);
        setPanelDecisionOpened(true);
        return;
      }
      if (reviewRequired) {
        setPreviewDraft(saved);
        setPreviewOpened(true);
        return;
      }
      await completeSignature(saved);
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "The addendum could not be prepared for signing."
      );
    }
  }, [
    caseId,
    completeSignature,
    form.values.contentHtml,
    persistDraft,
    reviewRequired,
  ]);

  const handleCancelPanelDecision = useCallback(() => {
    setPanelDecisionOpened(false);
    setPanelDecisionDraft(null);
  }, []);

  const handleContinueAfterPanelDecision = useCallback(
    async (acceptedKeys: string[]) => {
      if (!panelDecisionDraft) return;
      setPanelDecisionSubmitting(true);
      try {
        const acceptedReflex = reflexProposals.filter((item) =>
          acceptedKeys.includes(item.proposalKey)
        );
        if (acceptedReflex.length > 0) {
          await reflexPanelsService.acceptProposals(
            caseId,
            acceptedReflex.map((item) => ({
              proposalKey: item.proposalKey,
              enrollmentId: item.enrollmentId,
              reflexRuleId: item.reflexRuleId,
              specimenId: item.specimenId,
            }))
          );
        }
        const acceptedTissueCypher = tissueCypherProposals
          .filter((item) => acceptedKeys.includes(item.proposalKey))
          .map((item) => item.proposalKey);
        if (acceptedTissueCypher.length > 0) {
          await reflexPanelsService.acceptTissueCypherProposals(
            caseId,
            acceptedTissueCypher,
            panelDecisionDraft.id,
            tissueCypherProposals.some(
              (proposal) => proposal.requiresExplicitSpecimenSelection
            )
          );
        }
        const allReviewedKeys = [
          ...reflexProposals.map((item) => item.proposalKey),
          ...tissueCypherProposals.map((item) => item.proposalKey),
        ];
        setReviewedPanelKeys(allReviewedKeys);
        setPanelDecisionOpened(false);
        if (reviewRequired) {
          setPreviewDraft(panelDecisionDraft);
          setPreviewOpened(true);
        } else {
          await completeSignature(panelDecisionDraft, {
            reviewedKeys: allReviewedKeys,
          });
        }
      } catch {
        setFormError(
          "The selected panel could not be ordered. Review the case and try again."
        );
      } finally {
        setPanelDecisionSubmitting(false);
      }
    },
    [
      caseId,
      completeSignature,
      panelDecisionDraft,
      reflexProposals,
      reviewRequired,
      tissueCypherProposals,
    ]
  );

  const handleReviewedSignOut = useCallback(
    async (previewEvidence?: ReportPreviewEvidence) => {
      if (!previewDraft) return;
      setPreviewOpened(false);
      await completeSignature(previewDraft, { previewEvidence });
    },
    [completeSignature, previewDraft]
  );

  const handleAuthenticate = useCallback(
    async (password: string) => {
      if (draftPendingSignature) {
        await completeSignature(draftPendingSignature, {
          password,
          previewEvidence: evidencePendingSignature,
        });
      }
    },
    [completeSignature, draftPendingSignature, evidencePendingSignature]
  );

  const handleRequestClose = useCallback(() => {
    if (form.isDirty()) {
      setDiscardConfirmationOpened(true);
      return;
    }
    onClose();
  }, [form, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmationOpened(false);
    onClose();
  }, [onClose]);

  const handleDelete = useCallback(async () => {
    const draft = draftQuery.data;
    if (!draft) return;
    try {
      await deleteDraft.mutateAsync(draft.id);
      setDeleteConfirmationOpened(false);
      notifications.show({
        title: "Draft deleted",
        message: "The unsigned addendum draft was removed.",
        color: "green",
      });
      onClose();
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "The draft could not be deleted."
      );
    }
  }, [deleteDraft, draftQuery.data, onClose]);
  const handleOpenDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpened(true);
  }, []);
  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpened(false);
  }, []);
  const handleCloseDiscardConfirmation = useCallback(() => {
    setDiscardConfirmationOpened(false);
  }, []);
  const handleCloseAuthentication = useCallback(() => {
    setAuthenticationOpened(false);
  }, []);
  const handleClosePreview = useCallback(() => {
    setPreviewOpened(false);
  }, []);

  return (
    <>
      <Modal
        opened={opened && !previewOpened}
        onClose={handleRequestClose}
        title={`Add Addendum — ${caseNumber}`}
        size="90vw"
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
        closeButtonProps={{ "aria-label": "Close addendum editor" }}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            height: "92vh",
          },
          body: {
            display: "flex",
            flex: 1,
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            padding: 0,
          },
        }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} px="lg" py="md">
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {editorGuidance}
            </Text>
            {displayedError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                title="Addendum needs attention"
              >
                {displayedError}
              </Alert>
            )}
            <Select
              label="Addendum macro"
              placeholder="Optional — choose a saved macro"
              data={macroOptions}
              value={form.values.sourceMacroId}
              onChange={handleMacroChange}
              searchable
              clearable
              disabled={!isDraftHydrated}
              nothingFoundMessage="No active addendum macros"
            />
            <Box ref={addendumEditorRef}>
              <RichTextDiagnosis
                label="Addendum report text"
                withAsterisk
                value={form.values.contentHtml}
                onChange={handleContentChange}
                placeholder="Enter the addendum text."
                minHeight={420}
                disabled={!isDraftHydrated}
                error={form.errors.contentHtml as string | undefined}
                supplementalTypography="toolbar"
                paragraphSpacing="single-line"
              />
            </Box>
            <Text size="xs" c="dimmed">
              After selecting a macro, use Tab and Shift+Tab to move between its
              fillable fields.
            </Text>
            <MultiSelect
              label="Additional CPT codes"
              description="Optional additions; original signed codes are preserved."
              data={cptOptions}
              searchable
              clearable
              disabled={!isDraftHydrated}
              value={form.values.additionalCptCodeIds}
              onChange={handleCptCodesChange}
            />
            <AddendumCptQuantityRows
              selectedIds={form.values.additionalCptCodeIds}
              options={optionsQuery.data?.cptCodes || []}
              quantities={form.values.additionalCptCodeQuantities}
              disabled={!isDraftHydrated}
              validationRequested={Boolean(
                form.errors.additionalCptCodeQuantities
              )}
              onChange={handleCptQuantityChange}
              onBlur={handleCptQuantityBlur}
            />
            <MultiSelect
              label="Additional ICD-10 codes"
              description="Optional additions; original signed codes are preserved."
              data={icdOptions}
              searchable
              clearable
              disabled={!isDraftHydrated}
              {...form.getInputProps("additionalIcdCodeIds")}
            />
          </Stack>
        </ScrollArea>
        <Group
          justify="space-between"
          px="lg"
          py="md"
          style={{
            borderTop: "1px solid var(--mantine-color-gray-3)",
            flexShrink: 0,
          }}
        >
          <Group>
            {draftQuery.data && !draftOnly && (
              <Button
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={handleOpenDeleteConfirmation}
              >
                Delete Draft
              </Button>
            )}
          </Group>
          <Group>
            <Button
              variant="light"
              loading={saveDraft.isPending}
              disabled={!isDraftHydrated}
              onClick={handleSave}
            >
              Save Draft
            </Button>
            {!draftOnly && (
              <>
                <Button
                  variant="default"
                  leftSection={<IconEye size={16} />}
                  loading={saveDraft.isPending}
                  disabled={!isDraftHydrated}
                  onClick={handlePreview}
                >
                  Preview Addendum
                </Button>
                <Button
                  leftSection={<IconFileCheck size={16} />}
                  loading={isBusy}
                  disabled={!isDraftHydrated}
                  onClick={handleSignOut}
                >
                  Sign Out Addendum
                </Button>
              </>
            )}
          </Group>
        </Group>
      </Modal>
      {!draftOnly && (
        <ConfirmAddendumActionModal
          opened={deleteConfirmationOpened}
          title="Delete unsigned addendum draft?"
          message="This removes only the unsigned draft. It does not change any signed report."
          confirmLabel="Delete Draft"
          loading={deleteDraft.isPending}
          onCancel={handleCloseDeleteConfirmation}
          onConfirm={handleDelete}
        />
      )}
      <ConfirmAddendumActionModal
        opened={discardConfirmationOpened}
        title="Discard unsaved changes?"
        message="Changes made since the last draft save will be lost."
        confirmLabel="Discard Changes"
        onCancel={handleCloseDiscardConfirmation}
        onConfirm={handleDiscard}
      />
      {!draftOnly && (
        <SignOffAuthModal
          opened={authenticationOpened}
          onClose={handleCloseAuthentication}
          onAuthenticate={handleAuthenticate}
          isLoading={signOut.isPending}
          error={authenticationError}
        />
      )}
      {!draftOnly && previewDraft && (
        <AddendumReportPreviewModal
          opened={opened && previewOpened}
          caseId={caseId}
          draft={previewDraft}
          onClose={handleClosePreview}
          onSignOut={handleReviewedSignOut}
          signOutPending={signOut.isPending}
        />
      )}
      {!draftOnly && (
        <ReflexPanelDecisionModal
          key={[
            ...reflexProposals.map((item) => item.proposalKey),
            ...tissueCypherProposals.map((item) => item.proposalKey),
          ].join("|")}
          opened={opened && panelDecisionOpened}
          proposals={reflexProposals}
          tissueCypherProposals={tissueCypherProposals}
          submitting={panelDecisionSubmitting}
          onCancel={handleCancelPanelDecision}
          onContinue={handleContinueAfterPanelDecision}
        />
      )}
    </>
  );
}
